<template>
  <div class="app-container dashboard-page">
    <header class="dashboard-header">
      <div class="dashboard-title-clock">
        <button type="button" class="cal-current" @click="openCalendar">
          <span class="cal-current-text">{{ formattedDate }}</span>
          <span class="triangle" />
        </button>
        <!-- 地区選択（PC・複数選択）：カレンダーと現在時刻の間に配置 -->
        <RegionMultiPicker
          ref="headerRegionPickerRef"
          :area-ids="selectedAreaIds"
          :prefecture-ids="selectedPrefectureIds"
          :nearby="selectedNearby"
          @confirm="onRegionConfirm"
        />
        <div class="clock" aria-label="現在時刻">
          <i class="fas fa-clock clock-icon" aria-hidden="true" />
          <span class="clock-time">{{ clockText }}</span>
        </div>
      </div>

      <div class="dashboard-title-group">
        <button type="button" class="button dashboard-title-togglefullscreen" @click="toggleFullscreen">
          {{ inFullscreen ? '全画面解除' : '全画面' }}
        </button>
      </div>
    </header>

    <!-- 作品ランキング：行クリックで作品詳細ページ（作品詳細＋劇場別ランキング）が全画面で開く -->
    <MovieContent
      v-show="!showCalendar"
      ref="movieContentRef"
      :selected-date="selectedEnd"
      :start-date="selectedStart"
      :paused="showCalendar"
      :force-refresh-key="forceRefreshKey"
      :soft-refresh-key="softRefreshKey"
      :area-ids="selectedAreaIds"
      :prefecture-ids="selectedPrefectureIds"
      :nearby="selectedNearby"
      :region-label="regionLabel"
      :opened-cinema-id="openedCinema?.cinemaId ?? null"
      @change-date="onChangeDate"
      @open-cinema="onOpenCinema"
      @close-detail="closeWeeklyDrawer"
    />

    <!-- 劇場週次詳細：作品詳細ページの上に右からスライドインする（z-index は詳細ページより高い） -->
    <CinemaWeeklyDrawer
      :open="weeklyDrawerOpen"
      :cinema="openedCinema"
      :movie-id="openedMovieId"
      :version-params="weeklyVersionParams"
      @close="closeWeeklyDrawer"
    />

    <div v-if="showCalendar" class="calendar-container">
      <DashboardCalendar
        :select-start-date="selectedStartStr"
        :select-end-date="selectedEndStr"
        :select-min-date="minDate"
        :select-max-date="maxDate"
        @back="onCalendarBack"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import { MAX_RANGE_MONTHS, formatRangeLabel } from '@/utils/dashboard-date-range'
import CinemaWeeklyDrawer from './components/cinema-weekly-drawer.vue'
import DashboardCalendar from './components/dashboard-calendar.vue'
import MovieContent from './components/movie-content.vue'
import RegionMultiPicker from './components/region-multi-picker.vue'

dayjs.locale('ja')

const inFullscreen = ref(false)
const showCalendar = ref(false)
/**
 * 業務日レンジ。単日モードは start === end で表現し、期間モードのみ別日になる。
 * 期間は最長3か月（判定は utils/dashboard-date-range に集約、バックエンドと同条件）。
 */
const selectedStart = ref(dayjs())
const selectedEnd = ref(dayjs())
const clockNow = ref(dayjs())
const forceRefreshKey = ref(0)
const softRefreshKey = ref(0)

/** 地区選択（複数選択、共通状態）：作品ランキング・埋め込みの劇場別ランキングで共有 */
const selectedAreaIds = ref([])
const selectedPrefectureIds = ref([])
/** 「近隣50km」モード。地方・都道府県とは排他で、true のとき上の2つは必ず空 */
const selectedNearby = ref(false)
const regionLabel = ref('全国')
const headerRegionPickerRef = ref(null)
const movieContentRef = ref(null)

/** 週次詳細ドロワー（劇場別ランキングは作品詳細ドロワー内に埋め込み済みのため、状態はここだけで管理） */
const openedCinema = ref(null)
const openedMovieId = ref(null)
const weeklyDrawerOpen = ref(false)
/** ランキングと週次詳細で上映バージョン条件を必ず揃える */
const weeklyVersionParams = ref({})

const minDate = '2011-1-1'
// 先行販売（未来日）も見られるようにするため、上限は「今日」ではなく最長期間分だけ先まで許容する
const maxDate = dayjs().add(MAX_RANGE_MONTHS, 'month').format('YYYY-M-D')

let timer = null

const formattedDate = computed(() => formatRangeLabel(selectedStart.value, selectedEnd.value))
const selectedStartStr = computed(() => selectedStart.value.format('YYYY-M-D'))
const selectedEndStr = computed(() => selectedEnd.value.format('YYYY-M-D'))
const clockText = computed(() => clockNow.value.format('HH:mm:ss'))

function openCalendar() {
  // カレンダーオーバーレイ表示時に地区パネルが開いていれば閉じる
  headerRegionPickerRef.value?.close()
  showCalendar.value = true
}

function onRegionConfirm({ areaIds, prefectureIds, nearby, label }) {
  selectedAreaIds.value = areaIds
  selectedPrefectureIds.value = prefectureIds
  selectedNearby.value = Boolean(nearby)
  regionLabel.value = label
}

function onOpenCinema({ cinema, movieId }) {
  openedCinema.value = cinema
  openedMovieId.value = movieId
  // ドロワーを開く時点のバージョン条件を固定して渡す（ランキングと数字を一致させるため）
  weeklyVersionParams.value = movieContentRef.value?.currentVersionParams?.() ?? {}
  weeklyDrawerOpen.value = true
}

function closeWeeklyDrawer() {
  weeklyDrawerOpen.value = false
  openedCinema.value = null
}

/**
 * 前日/翌日ボタン（単日モードのみ表示）。未来日も許容する——先行販売の予測値を見せるため。
 */
function onChangeDate(date) {
  const next = dayjs(date)
  if (!next.isValid()) return
  selectedStart.value = next
  selectedEnd.value = next
  // 前日/翌日：静默刷新，不展示骨架屏
  softRefreshKey.value += 1
}

/**
 * カレンダーからの確定。単日は {start,end} が同日、期間は別日で返ってくる。
 * 引数なし（＝「戻る」）のときは選択を変えずに閉じるだけ。
 */
function onCalendarBack(picked) {
  if (!picked?.start || !picked?.end) {
    showCalendar.value = false
    return
  }
  const start = dayjs(picked.start)
  const end = dayjs(picked.end)
  if (start.isValid() && end.isValid()) {
    selectedStart.value = start
    selectedEnd.value = end
    forceRefreshKey.value += 1
  }
  showCalendar.value = false
}

function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen?.()
    inFullscreen.value = true
  } else {
    document.exitFullscreen?.()
    inFullscreen.value = false
  }
}

function onFullscreenChange() {
  inFullscreen.value = Boolean(document.fullscreenElement)
}

onMounted(() => {
  timer = window.setInterval(() => {
    clockNow.value = dayjs()
  }, 1000)
  document.addEventListener('fullscreenchange', onFullscreenChange)
})

onUnmounted(() => {
  if (timer) window.clearInterval(timer)
  document.removeEventListener('fullscreenchange', onFullscreenChange)
})
</script>

<style lang="less" scoped>
.dashboard-page {
  /* movie-content テーマ変数：.dashboard-page で定義することで、
     Teleport で header に移動する地区ピッカーにも継承させる */
  --mc-bg: var(--bg-panel, #fff);
  --mc-bg-soft: color-mix(in srgb, var(--brand-color, #f9da49) 6%, var(--bg-panel, #fff));
  --mc-row: var(--bg-panel, #fff);
  /* 斑马纹基于 panel，避免 dark 下奇数行走 --bg-body 形成“破洞”感 */
  --mc-row-alt: color-mix(in srgb, var(--brand-color, #f9da49) 5%, var(--bg-panel, #fff));
  --mc-row-selected: color-mix(in srgb, var(--brand-color, #f9da49) 22%, var(--bg-panel, #fff));
  --mc-row-hover: color-mix(in srgb, var(--brand-color, #f9da49) 12%, var(--bg-panel, #fff));
  --mc-text: var(--text-primary, #000);
  --mc-text-muted: var(--text-secondary, #4a4a4a);
  --mc-accent: var(--brand-color, #f9da49);
  /* 興行収入(AI推測値) / 総興行収入 の強調数値：テーマ主色から派生した偏移色（濃いめの赤） */
  --mc-accent-strong: var(--el-color-primary-dark-2, #a31d15);
  --mc-border: var(--border-color, #e5e5e5);
  --mc-scrollbar-thumb: color-mix(in srgb, var(--brand-color, #f9da49) 38%, #b8bdc4);
  --mc-scrollbar-thumb-hover: color-mix(in srgb, var(--brand-color, #f9da49) 58%, #9aa0a6);
  --mc-scrollbar-thumb-active: color-mix(in srgb, var(--brand-color, #f9da49) 72%, #858b92);

  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 40px);
  min-height: 0;
  padding: 12px 16px 20px;
  background: var(--bg-body, #fff);
  overflow: hidden;
}

/* 暗色模式：提高行对比，选中态用更可读的品牌色叠加 */
html.dark .dashboard-page {
  /* 深色模式：強調数値はテーマ赤の明るめ派生色（暗背景で視認性確保） */
  --mc-accent-strong: var(--el-color-primary-light-3, #db665f);
  --mc-bg-soft: color-mix(in srgb, var(--brand-color, #f9da49) 8%, var(--bg-panel, #20242b));
  --mc-row: var(--bg-panel, #20242b);
  --mc-row-alt: color-mix(in srgb, #ffffff 4.5%, var(--bg-panel, #20242b));
  --mc-row-selected: color-mix(in srgb, var(--brand-color, #f9da49) 18%, var(--bg-panel, #20242b));
  --mc-row-hover: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #20242b));
  --mc-scrollbar-thumb: color-mix(in srgb, var(--brand-color, #f9da49) 28%, #6b717a);
  --mc-scrollbar-thumb-hover: color-mix(in srgb, var(--brand-color, #f9da49) 48%, #8a9099);
  --mc-scrollbar-thumb-active: color-mix(in srgb, var(--brand-color, #f9da49) 62%, #a0a6ad);
}

.dashboard-header {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 56px;
  padding: 0 16px;
  font-size: 14px;
  line-height: 56px;
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
}

.dashboard-title-clock {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 4px;
  color: var(--text-secondary);
  font-size: 14px;
}

.cal-current {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid color-mix(in srgb, var(--brand-color) 45%, var(--border-color));
  border-radius: 16px;
  background: color-mix(in srgb, var(--brand-color) 18%, var(--bg-panel));
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: color-mix(in srgb, var(--brand-color) 28%, var(--bg-panel));
    border-color: var(--brand-color);
  }
}

.cal-current-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.clock {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  white-space: nowrap;

  .clock-icon {
    display: none;
    font-size: 11px;
    opacity: 0.75;
  }

  .clock-time {
    font-variant-numeric: tabular-nums;
  }
}

.triangle {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-top: -2px;
  border-top: 1px solid var(--text-primary);
  border-left: 1px solid var(--text-primary);
  transform: scaleY(-1) rotate(45deg);
  vertical-align: middle;
}

.dashboard-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  font-size: 13px;
}

.button {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  border: 1px solid color-mix(in srgb, var(--brand-color) 40%, var(--border-color));
  border-radius: 16px;
  background: color-mix(in srgb, var(--brand-color) 8%, var(--bg-panel));
  color: var(--text-primary);
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: color-mix(in srgb, var(--brand-color) 22%, var(--bg-panel));
    border-color: var(--brand-color);
  }
}

.calendar-container {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: color-mix(in srgb, #000 35%, transparent);
  backdrop-filter: blur(2px);
}

html.dark .calendar-container {
  background-color: color-mix(in srgb, #000 55%, transparent);
}

/* 对齐 layout mobile 断点（992px）：扣除顶部 mobile header，避免底部被裁切 */
@media (max-width: 992px) {
  .dashboard-page {
    height: calc(100vh - var(--mobile-header-height, 72px) - 12px);
    height: calc(100dvh - var(--mobile-header-height, 72px) - 12px);
    min-height: 0;
    /* iPhone 刘海/状态栏遮住顶部：用 safe-area-inset-top 把头部推到下方 */
    padding: calc(8px + env(safe-area-inset-top, 0px)) 10px 12px;
    gap: 8px;
    overflow: hidden;
  }

  .dashboard-header {
    height: auto;
    min-height: 48px;
    padding: 10px;
    line-height: 1.3;
    border-radius: 12px;
  }

  .dashboard-title-clock {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 0;
    min-width: 0;
    flex: 1;
    font-size: 13px;
  }

  .cal-current {
    flex: 1;
    min-width: 0;
    max-width: none;
    width: auto;
    justify-content: space-between;
    margin-right: 0;
    padding: 0 12px;
    height: 38px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 19px;
  }

  .clock {
    flex-shrink: 0;
    gap: 5px;
    height: 38px;
    padding: 0 12px;
    border: 1px solid var(--border-color);
    border-radius: 19px;
    background: color-mix(in srgb, var(--text-primary) 4%, var(--bg-panel));
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;

    .clock-icon {
      display: inline-block;
    }

    .clock-time {
      letter-spacing: 0.02em;
    }
  }
}

@media (max-width: 768px) {
  .dashboard-title-togglefullscreen {
    display: none;
  }

  .dashboard-title-group:empty {
    display: none;
  }

  /* スマホもヘッダーに地区ピッカーを表示：カレンダー/時計と高さを揃える */
  .dashboard-title-clock :deep(.region-multi) {
    flex-shrink: 0;
  }
  .dashboard-title-clock :deep(.region-multi__trigger) {
    height: 38px;
    max-width: 160px;
    border-radius: 19px;
  }
}

html.dark .dashboard-header {
  @media (max-width: 992px) {
    .clock {
      background: color-mix(in srgb, #ffffff 6%, var(--bg-panel));
      border-color: color-mix(in srgb, #ffffff 10%, var(--border-color));
    }
  }
}
</style>
